import React, { useState, useCallback, useEffect } from 'react';
import apiService from '../../services/apiService';
import useModal from '../../hooks/useModal';
import { AuthenticatedThumb } from './GaleriaGrid';

/**
 * Ítem seleccionable del picker de galería.
 *
 * @param {Object} props
 * @param {Object} props.foto - Objeto de foto
 * @param {boolean} props.selected - Si está seleccionado
 * @param {Function} props.onSelect - Callback al seleccionar
 * @returns {JSX.Element}
 */
function PickerThumb({ foto, selected, onSelect }) {
  return (
    <div
      className={`galeria-picker-item${selected ? ' galeria-picker-item--selected' : ''}`}
      onClick={() => onSelect(foto)}
      title={foto.descripcion || `Foto #${foto.id}`}
    >
      <AuthenticatedThumb fotoId={foto.id} descripcion={foto.descripcion} />
      {selected && (
        <div className="galeria-picker-check"><i className="fas fa-check-circle" /></div>
      )}
    </div>
  );
}

/**
 * Modal selector de fotos de galería personal.
 * Muestra las fotos libres del usuario para adjuntar como evidencia.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Si el modal está abierto
 * @param {Function} props.onClose - Callback al cerrar
 * @param {Function} props.onSelect - Callback al seleccionar una foto; recibe el objeto foto
 * @returns {JSX.Element|null}
 */
function GaleriaPickerModal({ isOpen, onClose, onSelect }) {
  const { handleBackdropClick } = useModal(isOpen, onClose);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchFotos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getMisFotos({ limit: 50 });
      if (response.success) setFotos(response.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) { setSelectedId(null); fetchFotos(); }
  }, [isOpen, fetchFotos]);

  const handleConfirm = () => {
    const foto = fotos.find(f => f.id === selectedId);
    if (foto) { onSelect(foto); onClose(); }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container modal-container-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title"><i className="fas fa-images" /> Elegir de galería</h3>
          <button className="modal-close-btn" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="modal-body">
          {loading && (
            <div className="galeria-empty">
              <i className="fas fa-spinner fa-spin fa-2x" /><p>Cargando fotos…</p>
            </div>
          )}
          {!loading && fotos.length === 0 && (
            <div className="galeria-empty">
              <i className="fas fa-images fa-3x" />
              <p>No tienes fotos disponibles en tu galería</p>
              <span>Sube fotos desde Mi Galería primero</span>
            </div>
          )}
          {!loading && fotos.length > 0 && (
            <div className="galeria-grid">
              {fotos.map(foto => (
                <PickerThumb
                  key={foto.id}
                  foto={foto}
                  selected={foto.id === selectedId}
                  onSelect={f => setSelectedId(f.id)}
                />
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={!selectedId}>
            <i className="fas fa-check" /> Adjuntar foto
          </button>
        </div>
      </div>
    </div>
  );
}

export default GaleriaPickerModal;
